import React, { useEffect, useRef, useState } from 'react';
import { useFela } from '../..';
import { CollapsibleProps } from './CollapsibleProps';
import { AngleUpIcon, AngleDownIcon } from '../Icon';
import { WithTheme } from '../../Theme';

const ITEM_HEADER_ICON_SIZE = 20;

const defaultItemContentStyle = ({
  theme,
  transitionTimeValueMs,
}: WithTheme<Pick<CollapsibleProps, 'transitionTimeValueMs'>>) => ({
  color: theme.palette.color3.hex,
  transition: `height ${transitionTimeValueMs}ms ease-in-out`,
  overflow: 'hidden',
  textAlign: 'left',
  wordWrap: 'break-word',
  fontFamily: theme.typography.fontFamily.primary.main,
  fontSize: theme.typography.fontSize.m.rem,
  fontWeight: 400,
  lineHeight: theme.typography.lineHeight.m.value,
});

const defaultItemContentInner = ({ theme }: WithTheme) => ({
  padding: `0 ${theme.dimension.spacing.xs.rem} ${theme.dimension.spacing.xxs.rem}`,
});

const defaultItemContainerStyle = ({ theme }: WithTheme) => ({
  width: '100%',
  display: 'block',
  borderWidth: '1px 0 0',
  borderStyle: 'solid',
  padding: 0,
  boxSizing: 'border-box',
  position: 'relative',
  borderColor: theme.palette.color4.hex,
  color: theme.palette.color2.hex,
  fontSize: theme.typography.fontSize.m.rem,
  '&:last-of-type': {
    borderBottomWidth: '1px',
  },
});

const defaultItemHeaderStyle = ({ theme }: WithTheme) => ({
  cursor: 'pointer',
  display: 'flex',
  border: 'none',
  padding: theme.dimension.spacing.xs.rem,
  width: '100%',
  backgroundColor: 'transparent',
  justifyContent: 'space-between',
  alignItems: 'center',
  textAlign: 'left',
  color: theme.palette.color2.hex,
  fontFamily: theme.typography.fontFamily.primary.main,
  fontSize: theme.typography.fontSize.l.rem,
  fontWeight: 400,
  lineHeight: theme.typography.lineHeight.l.value,
});

const defaultItemHeaderIcon = ({ theme }: WithTheme) => ({
  paddingLeft: theme.dimension.spacing.xxs.rem,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const Collapsible = ({
  id,
  dmId = 'dm-collapsible',
  isInitiallyExpanded = false,
  header,
  headerAriaLabel,
  content,
  styles,
  onClick,
  transitionTimeValueMs = 200,
}: CollapsibleProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
  const [contentHeight, setContentHeight] = useState<number | string>(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const ariaControlsId = `content-${id || dmId}`; // todo: dmId should be removed, as soon as id prop is required (myt-7757)
  const { css } = useFela({ transitionTimeValueMs });
  const closingTimeout = useRef<any>();
  const openingTimeout = useRef<any>();

  const usePrevious = (value: any) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });

    return ref.current;
  };

  const previouslyExpanded = usePrevious(isExpanded);

  const handleClosingAnimation = () => {
    if (previouslyExpanded) {
      // only animate when collapsible item state was previously expanded

      if (closingTimeout.current) {
        clearTimeout(closingTimeout.current);
      }
      if (openingTimeout.current) {
        clearTimeout(openingTimeout.current);
      }

      if (content && contentRef.current && contentHeight !== 0) {
        // we need to set a short timeout to animate closing collapsible properly
        setContentHeight(contentRef.current.scrollHeight);
      }

      closingTimeout.current = setTimeout(() => {
        setContentHeight(0);
      }, 50);
    } else {
      setContentHeight(0);
    }
  };

  const handleOpeningAnimation = () => {
    if (!previouslyExpanded) {
      // only animate when collapsible item state was previously collapsed

      if (content && contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight);
      }
      if (openingTimeout.current) {
        clearTimeout(openingTimeout.current);
      }
      // we need to set the height to auto after the animation ends
      openingTimeout.current = setTimeout(() => {
        setContentHeight('auto');
      }, transitionTimeValueMs);
    } else {
      setContentHeight('auto');
    }
  };

  useEffect(() => {
    if (isExpanded) {
      handleOpeningAnimation();
    } else {
      handleClosingAnimation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIcon = () => {
    if (content) {
      const dmIdExpanded = dmId ? `${dmId}-icon-expanded` : undefined;
      const dmIdCollapsed = dmId ? `${dmId}-icon-collapsed` : undefined;
      // to support dynamic content, but also display animations properly,
      return (
        <span
          data-dmid="collapsible-icon"
          className={css(
            defaultItemHeaderIcon,
            styles && styles.itemHeaderIcon,
          )}>
          {isExpanded ? (
            <AngleUpIcon
              data-dmid={dmIdExpanded}
              id={`${id || dmId}-icon-expanded`} // todo: dmId should be removed, as soon as id prop is required (myt-7757)
              size={ITEM_HEADER_ICON_SIZE}
            />
          ) : (
            <AngleDownIcon
              data-dmid={dmIdCollapsed}
              id={`${id || dmId}-icon-collapsed`} // todo: dmId should be removed, as soon as id prop is required (myt-7757)
              size={ITEM_HEADER_ICON_SIZE}
            />
          )}
        </span>
      );
    }
    return null;
  };

  const onItemClick = () => {
    if (content) {
      const newState = !isExpanded;

      setIsExpanded(newState);
      onClick && onClick(newState);

      if (newState) {
        handleOpeningAnimation();
      } else {
        handleClosingAnimation();
      }
    }
  };

  return (
    <div
      data-designsystem
      data-dmid={`${dmId}-container`}
      className={css(
        defaultItemContainerStyle,
        styles && styles.itemContainer,
      )}>
      <button
        aria-label={headerAriaLabel}
        aria-expanded={isExpanded}
        aria-controls={ariaControlsId}
        type="button"
        className={css(defaultItemHeaderStyle, styles && styles.itemHeader)}
        data-dmid={`${dmId}-header`}
        onClick={() => onItemClick()}>
        {header}
        {getIcon()}
      </button>
      {content && (
        <div
          aria-hidden={!isExpanded}
          id={ariaControlsId}
          data-dmid={`${dmId}-content`}
          className={css(defaultItemContentStyle, styles && styles.itemContent)}
          style={{ height: contentHeight }}
          ref={contentRef}>
          <div
            data-dmid={`${dmId}-inner-content`}
            className={css(
              defaultItemContentInner,
              styles && styles.itemContentInner,
            )}>
            {content}
          </div>
        </div>
      )}
    </div>
  );
};
export default Collapsible;
